'use client'

import { startTransition, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import Select, {
  ClassNamesConfig,
  components,
  ControlProps,
  DropdownIndicatorProps,
  InputProps,
  MenuProps,
  OptionProps,
  FilterOptionOption,
  SingleValueProps,
  StylesConfig,
  ValueContainerProps,
} from 'react-select'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { CheckIcon, ChevronDownIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import { useRouter } from 'next/navigation'

import { Lang } from '@/i18n'
import { Country } from '@/types'
import CountryFlag from '@/components/country-flag/country-flag'

import { encodeDestination, filterDestination } from './offers/helpers'
import { appendUTMToUrl } from '@/lib/utm'
import { Spinner3 } from '@/components/icons/spinner'

interface SearchbarProps {
  lang: Lang
  loadOnOpen?: boolean
}

type IsMulti = false

const instanceId = '2'
const countryQueryStringName = 'codes'

const styles: StylesConfig<Country, IsMulti> = {
  menuPortal: base => ({ ...base, zIndex: 9999 }),
}

const classNames: ClassNamesConfig<Country, IsMulti> = {
  menu: () => `!z-[99999] !mt-2 !rounded-2xl !overflow-hidden !shadow-xl`,
  control: () => `!min-h-[48px] !rounded-full !border-0 !bg-white !shadow-[0_8px_30px_rgba(15,23,42,0.14)] sm:!min-h-[72px]`,
  valueContainer: () => `!pl-4 !pr-2 !py-2`,
  menuList: props => {
    return props.selectProps.inputValue ? `` : `min-h-[500px] overflow-y-auto`
  },
  placeholder: () => '!text-slate-400 !text-[15px] sm:!text-[17px] truncate',
  input: () => '!text-slate-800 !text-[15px] sm:!text-[17px]',
  indicatorsContainer: () => `!pr-6`,
  indicatorSeparator: () => `!hidden`,
  dropdownIndicator: () => `!p-1 !text-slate-400`,
}

const Control = ({ children, ...props }: ControlProps<Country, IsMulti>) => {
  return (
    <components.Control {...props}>
      <MagnifyingGlassIcon className='ml-6 h-5 w-5 shrink-0 text-slate-400 sm:ml-7 sm:h-[22px] sm:w-[22px]' />
      {children}
    </components.Control>
  )
}

const DropdownIndicator = (props: DropdownIndicatorProps<Country, IsMulti>) => {
  return (
    <components.DropdownIndicator {...props}>
      <ChevronDownIcon className='mr-1 h-5 w-5 sm:h-[22px] sm:w-[22px]' />
    </components.DropdownIndicator>
  )
}

const Menu = (props: MenuProps<Country, IsMulti>) => {
  return <components.Menu {...props}>{props.children}</components.Menu>
}

const ValueContainer = (props: ValueContainerProps<Country, IsMulti>) => {
  return <components.ValueContainer {...props}>{props.children}</components.ValueContainer>
}

const SingleValue = (props: SingleValueProps<Country, IsMulti>) => {
  return (
    <components.SingleValue {...props} className='!text-gray-400'>
      {props.selectProps.placeholder}
    </components.SingleValue>
  )
}

const Option = (props: OptionProps<Country, IsMulti>) => {
  const { children, innerProps, ...rest } = props
  return (
    <div className={clsx('relative flex items-center pl-3 pr-9 py-2', props.isSelected ? 'font-bold text-black bg-gray-100 hover:bg-gray-200' : 'hover:bg-gray-200')} {...innerProps}>
      <div className='flex items-center gap-4'>
        <CountryFlag country={props.data} />
        {children}
      </div>
      {props.isSelected && (
        <span className='absolute top-0 bottom-0 right-0 flex items-center pr-4'>
          <CheckIcon className='size-5' />
        </span>
      )}
    </div>
  )
}

const NoOptionsMessage = () => {
  const { t } = useTranslation()
  return (
    <div className='p-4 md:p-6 w-full flex items-center flex-wrap justify-center gap-10'>
      <div className='grid gap-4'>
        <div>
          <h2 className='text-center text-black text-base font-semibold leading-relaxed pb-1'>{t('Search.Heading.NoResults')}</h2>
          <p className='text-center text-black text-sm font-normal leading-snug pb-4'>{t('Search.Text.NoResults')}</p>
          <div className='flex justify-center gap-3'>
            <a href='mailto:support@esimfly.app'>
              <button className='px-4 py-2 bg-white text-red-500 border border-red-500 rounded-full transition-all duration-500'>{t('Search.Action.NoResultsContact')}</button>
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}

const Input = ({ children, ...props }: InputProps<Country, IsMulti>) => {
  return <components.Input {...props} aria-activedescendant='' />
}

const Searchbar = ({ loadOnOpen = false }: SearchbarProps) => {
  const { t, i18n } = useTranslation()
  const router = useRouter()
  const placeholder = t('Landing.Text.SearchbarPlaceholder')
  const [country, setCountry] = useState<Country | null>(null)
  const [menuPortalTarget, setMenuPortalTarget] = useState<HTMLElement | null>(null)
  const [options, setOptions] = useState<{ loading: boolean; countries: Country[]; regions: Country[] }>({ loading: false, countries: [], regions: [] })
  const fetchedRef = useRef(false)

  const fetchOptions = useCallback(async () => {
    if (fetchedRef.current) {
      return
    }

    fetchedRef.current = true
    setOptions(prevState => ({ ...prevState, loading: true }))

    try {
      const data = await fetch(`/api/countries?languages=en,bs,hr,sr`).then(res => res.json())
      setOptions({ loading: false, ...data })
    } catch {
      fetchedRef.current = false
      setOptions(prevState => ({ ...prevState, loading: false }))
    }
  }, [])

  useEffect(() => {
    setMenuPortalTarget(document.body)
    if (!loadOnOpen) {
      fetchOptions()
    }
  }, [fetchOptions, loadOnOpen])

  const handleChange = (country: Country) => {
    setCountry(country)
    startTransition(() => {
      const url = appendUTMToUrl(`/${i18n.language}/offers/${encodeDestination(country.name)}`)
      router.push(url)
    })
  }

  const optionsGrouped = useMemo(() => {
    let opt = [
      {
        label: t('Search.Text.AllRegions'),
        options: options.regions,
      },
      {
        label: t('Search.Text.AllCountries'),
        options: options.countries,
      },
    ]
    return opt
  }, [options])

  return (
    <div className='z-50'>
      {country && (
        <div className='absolute top-0 left-0 w-full h-full flex items-center justify-center bg-white/70 z-20'>
          <Spinner3 className='w-10 h-10' />
        </div>
      )}
      <Select
        id='searchbar'
        menuPortalTarget={menuPortalTarget}
        instanceId={instanceId}
        placeholder={placeholder}
        styles={styles}
        classNames={classNames}
        className='w-full'
        components={{
          Control,
          DropdownIndicator,
          Input,
          ValueContainer,
          SingleValue,
          Menu,
          Option,
          NoOptionsMessage,
        }}
        onFocus={() => {
          const isDesktop = window.innerWidth > 768
          if (isDesktop) {
            return
          }

          const el = document.getElementById('searchbar')
          if (!el) {
            return
          }

          setTimeout(() => {
            // const headerHeight = 72
            // const text = 100
            // const scrollTop = window.scrollY + headerHeight + text
            // window.scrollTo({ top: scrollTop, behavior: 'smooth' })
            const rect = el.getBoundingClientRect()
            const scrollTop = window.scrollY + rect.top - 20 // 20px offset from top
            window.scrollTo({ top: scrollTop, behavior: 'smooth' })
          }, 100) // wait for keyboard to appear
        }}
        onBlur={() => {
          const isDesktop = window.innerWidth > 768
          if (isDesktop) {
            return
          }

          const el = document.getElementById('searchbar')
          if (!el) {
            return
          }
          window.scrollTo({ top: 0, behavior: 'smooth' })
        }}
        onMenuOpen={() => {
          if (loadOnOpen) {
            fetchOptions()
          }
        }}
        filterOption={(option: FilterOptionOption<Country>, inputValue: string) => filterDestination(option.data, inputValue)}
        formatOptionLabel={data => data.name}
        getOptionValue={data => data.code}
        value={country}
        isLoading={options.loading}
        inputId={countryQueryStringName}
        options={optionsGrouped}
        onChange={(value, actionMeta) => {
          if (value) {
            handleChange(value)
          }
        }}
      />
    </div>
  )
}

export default Searchbar
